import { useEffect, useMemo, useRef } from 'react'
import { ActionType, type Chart, type OverlayCreate } from 'klinecharts'
import { asOverlayId } from './types'
import { registerWorkbenchOverlays, withAlpha } from './customOverlays'
import type { SavedDrawing } from './workbenchState'
import { TRADE_MARK } from './tradeIdeas'

/**
 * Ceiling on drawing overlays, mirroring BacktestChart's MAX_OVERLAYS: klinecharts walks
 * every overlay on each repaint and this chart repaints on every replay tick. A workbench
 * with 400+ timestamped drawings is well past the point where the walkthrough reads.
 */
const MAX_DRAWING_OVERLAYS = 400

/**
 * Opacity applied to geometry so the code's real orders stay dominant.
 *
 * Very dim on purpose. The drawn levels are quiet REFERENCE during a run — the thing the
 * price action and the live order lines are being compared against — and at half strength
 * they competed with the lines they exist to be checked against.
 */
const DEFAULT_DIM = 0.1

/**
 * Overlays whose geometry spans the full chart width, so they can be created regardless of
 * whether their timestamp falls inside the loaded candle window.
 */
const X_INDEPENDENT = new Set(['levelLine', 'horizontalStraightLine', 'horizontalRayLine'])

/**
 * Overlays an alpha rewrite here cannot reach, because they hardcode their colours inside
 * createPointFigures (customOverlays.ts) and never look at extendData.
 *
 * The legacy setup markers are stuck at full opacity as a result — no code creates them any
 * more, they only arrive via restore. `tradeMark` is here for the opposite reason: it manages
 * its OWN rest and lit alphas from `active` / `lineFocus`, so rewriting its colours would fight
 * the overlay rather than help it. See the hover-focus block below.
 */
const NEVER_DIMMED = new Set(['buyEntry', 'sellEntry', 'exitPoint', TRADE_MARK])

interface DrawingSpec {
  /** Stable across reconciles. ctxId when the drawing has one; geometry otherwise. */
  key: string
  /** unix SECONDS of the drawing's earliest point. */
  revealTs: number
  /** ms bounds, for the loaded-window containment check. */
  minMs: number
  maxMs: number
  xIndependent: boolean
  /**
   * The trade idea this drawing belongs to, when it is one of an idea's levels.
   *
   * Read off the saved extendData once, so the hover group lookup never has to ask the engine
   * what an overlay is. Undefined for every other kind of drawing, which is also what keeps
   * them out of the hover machinery entirely.
   */
  ideaId?: string
  create: OverlayCreate
}

/**
 * A trade level's extendData with the hover flag set.
 *
 * `overrideOverlay` REPLACES extendData rather than merging it, so both the create path and
 * the hover applier rebuild the whole object from the spec — and they have to agree, which is
 * why this exists in one place.
 */
function focusedExtendData(spec: DrawingSpec, lit: boolean): Record<string, unknown> {
  return { ...(spec.create.extendData as Record<string, unknown> | undefined), lineFocus: lit }
}

export interface UseWalkthroughDrawingsOverlayOptions {
  /** Raw engine of the REPLAY chart. Null until it mounts. */
  chart: Chart | null
  /** Serialized drawings, frozen when walkthrough mode was entered. Timestamps are ms. */
  drawings: SavedDrawing[] | null
  /** The replay playhead, unix SECONDS (progressViewTs). */
  revealTs: number
  /** Changes whenever the engine's series changes, so pagination triggers a reconcile. */
  dataKey: string
  /** Alpha for geometry colours; 1 leaves them untouched. */
  dim?: number
  enabled?: boolean
}

/** Rewrite the colours in a style subtree at the given alpha. */
function dimStyles(
  styles: SavedDrawing['styles'] | undefined,
  alpha: number,
): SavedDrawing['styles'] | undefined {
  if (!styles || alpha >= 1) return styles
  const out: Record<string, Record<string, unknown>> = {}
  for (const [group, values] of Object.entries(styles)) {
    if (!values || typeof values !== 'object') continue
    const next: Record<string, unknown> = { ...values }
    for (const prop of ['color', 'borderColor']) {
      const v = next[prop]
      if (typeof v === 'string' && v) next[prop] = withAlpha(v, alpha)
    }
    out[group] = next
  }
  return out as SavedDrawing['styles']
}

/** Rewrite the colours a custom overlay reads out of extendData. */
function dimExtendData(
  name: string,
  extendData: Record<string, unknown> | undefined,
  alpha: number,
): Record<string, unknown> | undefined {
  if (!extendData || alpha >= 1 || NEVER_DIMMED.has(name)) return extendData
  const out: Record<string, unknown> = { ...extendData }

  for (const prop of ['color', 'fillColor', 'connectorColor']) {
    const v = out[prop]
    if (typeof v === 'string' && v) out[prop] = withAlpha(v, alpha)
  }

  if (Array.isArray(out.levels)) {
    out.levels = (out.levels as Array<Record<string, unknown>>).map((lvl) =>
      typeof lvl?.color === 'string' && lvl.color
        ? { ...lvl, color: withAlpha(lvl.color, alpha) }
        : lvl,
    )
  }

  return out
}

/**
 * Repaint the user's saved drawings onto the walkthrough's replay chart, revealing each one
 * as the playhead reaches it.
 *
 * Three things make this safe to run next to the replay:
 *
 * 1. **A drawing is revealed at its EARLIEST point.** That is what puts plan next to reality
 *    at the moment it matters — the user's entry→exit line is on screen as the code takes its
 *    entry. The consequence is that a multi-point drawing paints whole, so its right-hand
 *    geometry extends past the playhead; that is the user's own drawing over their own drawn
 *    period, so it reveals nothing they don't already know.
 * 2. **Diff-by-key reconcile, never remove-all/create-all.** In the replay steady state the
 *    delta is zero or one overlay, and a no-op reconcile issues no engine calls at all.
 * 3. **These overlays are invisible to persistence.** The ids live in a hook-private map, on a
 *    different Chart instance from the one serializeSymbolSlice reads, and drawIdsRef is never
 *    touched — so autosave, the AI-context diff and "remove all drawings" cannot see them.
 */
export default function useWalkthroughDrawingsOverlay({
  chart,
  drawings,
  revealTs,
  dataKey,
  dim = DEFAULT_DIM,
  enabled = true,
}: UseWalkthroughDrawingsOverlayOptions): void {
  // key -> overlay id. Private on purpose (see the doc comment above).
  const idsRef = useRef<Map<string, string>>(new Map())
  /** The spec behind each live overlay, so the hover applier can rebuild its extendData. */
  const specsByKeyRef = useRef<Map<string, DrawingSpec>>(new Map())
  /**
   * The OVERLAY holding focus — hovered by a pointer, or tapped by a finger — or null. The lit
   * idea is derived from it.
   *
   * Keyed by overlay rather than by idea because that is the granularity the events arrive at,
   * and it is what makes moving between two levels of one idea work: the engine fires the next
   * level's enter before the previous one's leave, so a leave has to be able to ask "is the
   * pointer still on ME?" — a question "which idea is lit?" cannot answer when both levels
   * belong to the same idea.
   *
   * A ref, not state: lighting an idea is an `overrideOverlay` on a handful of overlays and
   * needs no React render — and a render here would land in the middle of the replay's own
   * 30fps repaint path.
   */
  const focusedKeyRef = useRef<string | null>(null)
  /**
   * Set by a level's own click, read and cleared by the chart-wide tap listener below.
   *
   * The two arrive on the SAME gesture — klinecharts dispatches the overlay's click from inside
   * its touchend handler, and the DOM event then bubbles on to the container — so without this
   * the tap that lit an idea would immediately be read as a tap somewhere else and put it back.
   */
  const ideaTapRef = useRef(false)

  const specs = useMemo<DrawingSpec[]>(() => {
    if (!drawings) return []
    registerWorkbenchOverlays() // idempotent

    const out: DrawingSpec[] = []
    drawings.forEach((d, i) => {
      let minMs = Infinity
      let maxMs = -Infinity
      for (const p of d.points ?? []) {
        const ts = p?.timestamp
        if (typeof ts !== 'number' || !Number.isFinite(ts)) continue
        if (ts < minMs) minMs = ts
        if (ts > maxMs) maxMs = ts
      }

      const ctxId = d.extendData?.ctxId
      const key = typeof ctxId === 'string' && ctxId
        ? ctxId
        : `${d.name}#${i}:${minMs}:${maxMs}`

      const styles = dimStyles(d.styles, dim)
      const extendData = dimExtendData(d.name, d.extendData, dim)
      const rawIdeaId = d.extendData?.ideaId
      const ideaId = d.name === TRADE_MARK && typeof rawIdeaId === 'string' && rawIdeaId
        ? rawIdeaId
        : undefined

      out.push({
        key,
        // A drawing with no timestamp at all can never be revealed by the playhead.
        revealTs: Number.isFinite(minMs) ? Math.floor(minMs / 1000) : Infinity,
        minMs,
        maxMs,
        xIndependent: X_INDEPENDENT.has(d.name),
        ideaId,
        create: {
          name: d.name,
          points: d.points,
          ...(styles ? { styles: styles as OverlayCreate['styles'] } : {}),
          // No `active` flag: a trade level rests QUIET and lights on hover, which is exactly
          // what it does on the canvas. Forcing it lit here — the previous rule, on the
          // argument that a plan you cannot read defeats the replay — put every idea at full
          // strength against the code's own order lines, and those lines are the thing the
          // plan is being compared with. The hover-focus block below is the read route now.
          ...(extendData ? { extendData } : {}),
          // Read-only, always: walkthrough mode must not let a drawing be dragged.
          lock: true,
          // Load-bearing: without onDrawEnd the engine enters interactive draw mode.
          onDrawEnd: () => true,
        },
      })
    })

    // Earliest first, so the MAX cap drops the drawings the user reaches last rather than an
    // arbitrary slice.
    out.sort((a, b) => a.revealTs - b.revealTs)
    return out
  }, [drawings, dim])

  useEffect(() => {
    const live = idsRef.current
    if (!chart) {
      // The engine is gone; its overlays went with it. Drop the ids so a remount starts clean.
      live.clear()
      specsByKeyRef.current = new Map()
      focusedKeyRef.current = null
      return undefined
    }

    /** The idea currently holding focus, or null. */
    const litIdea = (): string | null => {
      const key = focusedKeyRef.current
      if (key === null) return null
      return specsByKeyRef.current.get(key)?.ideaId ?? null
    }

    /**
     * Push the current focus onto every live trade level.
     *
     * `overrideOverlay`, never remove-and-recreate: a destroyed overlay can never deliver its
     * own `mouseleave`, so rebuilding the set in order to light one of them would lose the
     * un-hover for the very overlay the pointer is sitting on — and the idea would stay lit
     * with no way back to rest. Same trap, same answer, as the replay's order lines.
     */
    const applyIdeaFocus = () => {
      const lit = litIdea()
      for (const [key, id] of live) {
        const spec = specsByKeyRef.current.get(key)
        if (!spec?.ideaId) continue
        try {
          chart.overrideOverlay({ id, extendData: focusedExtendData(spec, spec.ideaId === lit) })
        } catch {
          /* engine disposed mid-apply */
        }
      }
    }

    const reconcile = () => {
      const list = chart.getDataList()
      if (list.length === 0) return
      const firstMs = list[0].timestamp
      const lastMs = list[list.length - 1].timestamp

      const wanted = new Map<string, DrawingSpec>()
      if (enabled) {
        for (const spec of specs) {
          if (spec.revealTs > revealTs) continue
          // klinecharts snaps an out-of-range timestamp to the nearest bar, which would bend
          // the shape flat onto the window's edge. Skip it; pagination brings it back on a
          // later reconcile (OnVisibleRangeChange also fires when new data is applied).
          if (!spec.xIndependent && (spec.minMs < firstMs || spec.maxMs > lastMs)) continue
          wanted.set(spec.key, spec)
          if (wanted.size >= MAX_DRAWING_OVERLAYS) break
        }
      }

      // Replaced wholesale rather than mutated, so an un-revealed drawing's spec is pruned for
      // free. Set before the create loop, because the hover seed reads it back.
      specsByKeyRef.current = wanted

      // A scrub back past the focused level takes its overlay with it, leaving nothing that
      // could ever deliver the un-hover. Drop the key with it rather than stranding it and
      // leaving every other idea unable to light.
      if (focusedKeyRef.current !== null && !wanted.has(focusedKeyRef.current)) {
        focusedKeyRef.current = null
      }

      for (const [key, id] of live) {
        if (!wanted.has(key)) {
          try {
            chart.removeOverlay(id)
          } catch {
            /* engine disposed mid-reconcile */
          }
          live.delete(key)
        }
      }

      for (const [key, spec] of wanted) {
        if (live.has(key)) continue
        try {
          const id = asOverlayId(chart.createOverlay(
            spec.ideaId
              ? {
                  ...spec.create,
                  // Seeded from the live hover state. The playhead reveals an idea's levels one
                  // at a time, so a level appearing while its own idea is hovered has to arrive
                  // already lit — otherwise it is the one part of a lit idea still at rest.
                  extendData: focusedExtendData(spec, spec.ideaId === litIdea()),
                  // Hover any level of an idea and the WHOLE idea lights: the levels are one
                  // plan, and lighting only the line under the cursor would show a stop with no
                  // entry. `lock: true` does not block these — the replay's order lines are
                  // locked and hover the same way.
                  onMouseEnter: () => {
                    focusedKeyRef.current = key
                    applyIdeaFocus()
                    return false
                  },
                  onMouseLeave: () => {
                    // Only the overlay that actually holds the focus may release it. Moving
                    // between two levels of one idea fires the next one's enter BEFORE this
                    // leave, so an unguarded clear would drop the idea just lit.
                    if (focusedKeyRef.current !== key) return false
                    focusedKeyRef.current = null
                    applyIdeaFocus()
                    return false
                  },
                  // A FINGER HAS NO HOVER. The tap is the whole gesture, so it stands in for
                  // enter — and the release comes from the chart-wide listener below rather than
                  // from a leave that will never arrive. klinecharts routes a tap through the
                  // same widget click event a mouse click takes, so this fires on both and the
                  // desktop simply gets what hover has already given it.
                  onClick: () => {
                    focusedKeyRef.current = key
                    applyIdeaFocus()
                    ideaTapRef.current = true
                    return false
                  },
                }
              : spec.create,
          ))
          if (id) live.set(key, id)
        } catch {
          /* engine disposed mid-reconcile */
        }
      }
    }

    reconcile()

    // Pagination and data application both surface here, which is how a drawing outside the
    // initial window eventually materialises.
    const onRange = () => reconcile()
    chart.subscribeAction(ActionType.OnVisibleRangeChange, onRange)

    /**
     * The other half of tap-to-light: a tap anywhere that is not a level puts the chart back to
     * rest, which is the only "leave" a touch device can offer.
     *
     * On the container, not on the canvas, so it catches every tap the chart receives — and
     * AFTER klinecharts, which dispatches the overlay's own click from inside its touchend
     * handler on a descendant. That ordering is what ideaTapRef is for.
     *
     * touchend only. A mouse keeps hover as its release, and clearing on desktop clicks as well
     * would fight it: the pointer is still sitting on the level it just un-lit.
     */
    const dom = chart.getDom()
    const onTouchEnd = () => {
      if (ideaTapRef.current) {
        ideaTapRef.current = false
        return
      }
      if (focusedKeyRef.current === null) return
      focusedKeyRef.current = null
      applyIdeaFocus()
    }
    dom?.addEventListener('touchend', onTouchEnd)

    return () => {
      chart.unsubscribeAction(ActionType.OnVisibleRangeChange, onRange)
      dom?.removeEventListener('touchend', onTouchEnd)
    }
  }, [chart, specs, revealTs, dataKey, enabled])

  // Sweep on unmount so a mode switch cannot leave orphans behind on a chart that outlives us.
  useEffect(() => {
    const live = idsRef.current
    return () => {
      const c = chart
      if (c) {
        for (const id of live.values()) {
          try {
            c.removeOverlay(id)
          } catch {
            /* already disposed */
          }
        }
      }
      live.clear()
      specsByKeyRef.current = new Map()
      focusedKeyRef.current = null
    }
  }, [chart])
}
